/**
 * CamerasList — cámaras IP (módulo CCTV).
 *
 * Listado estándar (`ListPage`) y **ficha de pantalla completa** (`CameraForm`),
 * igual que servidores, switches o ubicaciones. Antes el alta/edición era un
 * modal; ahora la ficha abre EN LECTURA con sus pestañas (General, Ubicación,
 * Contactos) y se edita con «Editar».
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { VideoCameraIcon, PlusIcon, MapPinIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import useNavOrder from '../hooks/useNavOrder';
import ListPage from './common/ListPage';
import CameraForm from './CameraForm';

const ESTADOS = [
  { value: 'active', label: 'Activa' },
  { value: 'inactive', label: 'Inactiva' },
  { value: 'maintenance', label: 'Mantenimiento' },
  { value: 'decommissioned', label: 'Dada de baja' },
];

export default function CamerasList({ companyId }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [recorders, setRecorders] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [filtroRecorder, setFiltroRecorder] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroUbicacion, setFiltroUbicacion] = useState('');
  const [hlId, setHlId] = useState(null);

  // Ficha abierta: { camera|null, mode: 'view'|'edit'|'create' }
  const [ficha, setFicha] = useState(null);

  const cargar = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const [cameras, grabadores, ubicaciones] = await Promise.all([
        api.getCameras(companyId),
        api.getRecorders(companyId),
        api.getLocations('', companyId),
      ]);
      setItems(cameras.data || []);
      setRecorders(grabadores.data || []);
      setLocations(ubicaciones.data || []);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar las cámaras');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !items.length) return undefined;
    const id = Number(raw);
    const target = items.find((item) => item.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { setFicha({ camera: target, mode: 'view' }); return undefined; }
    setHlId(id);
    const row = document.getElementById(`camera-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
  }, [searchParams, items, setSearchParams]);

  const ubicacionNombre = (id) => locations.find((loc) => loc.id === Number(id))?.name || '';

  const filtrados = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      const coincide = !q
        || (item.name || '').toLowerCase().includes(q)
        || (item.code || '').toLowerCase().includes(q)
        || (item.model || '').toLowerCase().includes(q)
        || (item.ip_address || '').toLowerCase().includes(q)
        || (item.mac_address || '').toLowerCase().includes(q)
        || (item.recorder_name || '').toLowerCase().includes(q)
        || (item.location_room || '').toLowerCase().includes(q);
      const grabador = !filtroRecorder
        || (filtroRecorder === 'none' ? !item.recorder_id : String(item.recorder_id) === filtroRecorder);
      const estado = !filtroEstado || item.status === filtroEstado;
      const ubicacion = !filtroUbicacion || String(item.location_id || '') === filtroUbicacion;
      return coincide && grabador && estado && ubicacion;
    });
  }, [items, search, filtroRecorder, filtroEstado, filtroUbicacion]);

  // Orden de las fichas congelado mientras la ficha está abierta.
  const navCameras = useNavOrder(filtrados, ficha?.camera || null, Boolean(ficha));

  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'name',
        label: 'Cámara',
        sortable: true,
        render: (item) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <VideoCameraIcon style={{ width: 15, height: 15, color: 'var(--accent-l)' }} />
            <strong>{item.name}</strong>
          </span>
        ),
      },
      { key: 'code', label: 'Código', sortable: true, render: (item) => (item.code ? <span className="list-code">{item.code}</span> : dash) },
      {
        key: 'recorder_name',
        label: 'Grabador',
        sortable: true,
        render: (item) => (item.recorder_name
          ? <span className="list-badge list-badge--accent">{item.recorder_name}</span>
          : <span className="list-badge list-badge--off">Sin grabador</span>),
      },
      { key: 'camera_mode_label', label: 'Tipo', sortable: true, render: (item) => item.camera_mode_label || dash },
      { key: 'model', label: 'Modelo', sortable: true, render: (item) => item.model || dash },
      {
        key: 'ip_address',
        label: 'IP',
        sortable: true,
        render: (item) => (item.ip_address ? <span className="list-code">{item.ip_address}</span> : dash),
      },
      { key: 'resolution', label: 'Resolución', sortable: true, render: (item) => item.resolution || dash },
      {
        key: 'location_name',
        label: 'Ubicación',
        sortable: true,
        sortValue: (item) => ubicacionNombre(item.location_id) || item.location_name || '',
        render: (item) => {
          const nombre = ubicacionNombre(item.location_id) || item.location_name;
          if (!nombre && !item.location_room) return dash;
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <MapPinIcon style={{ width: 13, height: 13, color: 'var(--text-muted)' }} />
              {nombre || '—'}{item.location_room ? ` · ${item.location_room}` : ''}
            </span>
          );
        },
      },
      {
        key: 'status',
        label: 'Estado',
        sortable: true,
        render: (item) => (
          <span className={`list-badge ${
            item.status === 'active' ? 'list-badge--on'
              : item.status === 'maintenance' ? 'list-badge--warn'
                : item.status === 'decommissioned' ? 'list-badge--muted' : 'list-badge--off'
          }`}>
            {item.status_label || item.status}
          </span>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  const exportRows = useMemo(
    () => filtrados.map((item) => ({
      Nombre: item.name,
      Código: item.code || '',
      Grabador: item.recorder_name || '',
      Tipo: item.camera_mode_label || '',
      Fabricante: item.manufacturer || '',
      Modelo: item.model || '',
      IP: item.ip_address || '',
      MAC: item.mac_address || '',
      Resolución: item.resolution || '',
      Ubicación: ubicacionNombre(item.location_id) || item.location_name || '',
      Sala: item.location_room || '',
      Estado: item.status_label || item.status,
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtrados, locations],
  );

  if (ficha) {
    return (
      <CameraForm
        camera={ficha.camera}
        companyId={companyId}
        recorders={recorders}
        locations={locations}
        initialMode={ficha.mode}
        navItems={navCameras}
        navIndex={ficha.camera?.id ? navCameras.findIndex(c => String(c.id) === String(ficha.camera.id)) : -1}
        onNavigate={(destino) => setFicha({ camera: destino, mode: ficha.mode })}
        onClose={() => setFicha(null)}
        onSaved={() => { cargar(); }}
        onDeleted={() => { setFicha(null); cargar(); }}
      />
    );
  }

  return (
    <ListPage
      icon={VideoCameraIcon}
      title="Cámaras"
      subtitle="Cámaras IP, su grabador, su IP (IPAM) y dónde están físicamente."
      companyId={companyId}
      search={search}
      onSearchChange={setSearch}
      searchPlaceholder="Buscar cámara, IP, MAC, grabador…"
      filters={[
        {
          key: 'recorder',
          label: 'Grabador',
          allLabel: 'Todos los grabadores',
          value: filtroRecorder,
          onChange: setFiltroRecorder,
          options: [
            { value: 'none', label: 'Sin grabador' },
            ...recorders.map((recorder) => ({ value: recorder.id, label: recorder.name })),
          ],
        },
        {
          key: 'status',
          label: 'Estado',
          allLabel: 'Todos los estados',
          value: filtroEstado,
          onChange: setFiltroEstado,
          options: ESTADOS,
        },
        {
          key: 'location',
          label: 'Ubicación',
          allLabel: 'Todas las ubicaciones',
          value: filtroUbicacion,
          onChange: setFiltroUbicacion,
          options: locations.map((loc) => ({ value: loc.id, label: loc.name })),
        },
      ]}
      columns={columns}
      columnStorageKey="cmdb_cols_cameras"
      listKey="cameras"
      rows={filtrados}
      rowId={(item) => `camera-row-${item.id}`}
      rowClassName={(item) => (item.id === hlId ? 'list-row-highlight' : '')}
      onRowClick={(item) => setFicha({ camera: item, mode: 'view' })}
      exportRows={exportRows}
      exportFileBase="camaras"
      loading={loading && items.length === 0}
      error={error}
      onCreate={() => setFicha({ camera: null, mode: 'create' })}
      createLabel="Nueva cámara"
      importEntity="cameras"
      onImported={cargar}
      empty={{
        icon: '📷',
        title: items.length === 0 ? 'Todavía no hay cámaras' : 'Ninguna cámara coincide',
        subtitle: items.length === 0
          ? 'Da de alta primero el grabador y luego sus cámaras. La IP se registra sola en el IPAM.'
          : undefined,
        action: items.length === 0 ? (
          <button type="button" className="list-btn-primary" onClick={() => setFicha({ camera: null, mode: 'create' })}>
            <PlusIcon style={{ width: 14, height: 14 }} /> Nueva cámara
          </button>
        ) : undefined,
      }}
      actions={{
        onEdit: (row) => setFicha({ camera: row, mode: 'edit' }),
        onDelete: async (row) => { await api.deleteCamera(row.id, companyId); await cargar(); },
        deleteTitle: 'Eliminar cámara',
        deleteMessage: (row) => `¿Eliminar la cámara «${row.name}»? También se libera su IP en el IPAM.`,
      }}
    />
  );
}
